// SearchDetail.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Loader2 } from 'lucide-react';
import { gsap } from 'gsap';
import '../../../assests/css/customer/SearchDetail.css';

const API_BASE_URL = 'http://localhost:8000/api';
  // const { baseUrl } = useContext(AppContext);


const SearchDetail = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const overlayRef = useRef(null);
  const sidebarRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    fetch(`${API_BASE_URL}/customer/product/search-suggestions`, {
      headers: { Accept: 'application/json' },
    })
      .then((res) => res.json())
      .then((data) => {
        setSuggestions(data.categories || []);
        setFeaturedProducts(data.featured || []);
      })
      .catch((err) => console.error('Suggestions error:', err));
  }, [isOpen]);

  const fetchResults = useCallback(async (query) => {
    if (!query || query.trim().length === 0) {
      setResults([]);
      setSubcategories([]);
      setSelectedSubcategory(null);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/customer/product/search?q=${encodeURIComponent(query)}`,
        { method: 'GET', headers: { Accept: 'application/json' } }
      );

      if (!response.ok) throw new Error('Search request failed');

      const data = await response.json();
      setSubcategories(data.subcategories || []);
      setResults(data.data || []);
    } catch (err) {
      console.error('Search error:', err);
      setError('Could not fetch results. Please try again.');
      setResults([]);
      setSubcategories([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchResults(searchQuery);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery, fetchResults]);

  useEffect(() => {
    if (isOpen) {
      if (!overlayRef.current || !sidebarRef.current) return;
      document.body.style.overflow = 'hidden';
      gsap.to(overlayRef.current, { opacity: 1, duration: 0.3, ease: 'power2.out' });
      gsap.fromTo(sidebarRef.current, { x: '100%' }, { x: '0%', duration: 0.4, ease: 'power3.out' });
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 400);
    } else {
      if (!overlayRef.current || !sidebarRef.current) return;
      document.body.style.overflow = 'unset';
      gsap.to(overlayRef.current, { opacity: 0, duration: 0.3, ease: 'power2.in' });
      gsap.to(sidebarRef.current, { x: '100%', duration: 0.4, ease: 'power3.in' });
      setSearchQuery('');
      setResults([]);
      setSubcategories([]);
      setSelectedSubcategory(null);
      setError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleSuggestionClick = (categoryName) => {
    setSelectedSubcategory(null);
    setSearchQuery(categoryName);
  };

  const handleSubcategoryClick = (sub) => {
    setSelectedSubcategory(sub);
    setSearchQuery(sub.title);
  };

  const handleProductClick = (product) => {
    onClose();
    navigate(`/product-detail/${product.id}`);
  };

  if (!isOpen) return null;

  const visibleResults = selectedSubcategory
    ? results.filter((p) => p.mlsubcategory_id === selectedSubcategory.id)
    : results;

  const renderProductCard = (product) => {
    const hasDiscount =
      product.discount_price &&
      Number(product.discount_price) > 0 &&
      Number(product.discount_price) < Number(product.base_price);

    return (
      <div
        key={product.id}
        className="Customer-SearchBar-result-item"
        onClick={() => handleProductClick(product)}
      >
        <div className="Customer-SearchBar-result-image">
          {product.image ? (
            <img src={product.image} alt={product.name} />
          ) : (
            <div className="Customer-SearchBar-result-image-placeholder" />
          )}
        </div>
        <div className="Customer-SearchBar-result-info">
          <h4 className="Customer-SearchBar-result-name">{product.name}</h4>
          <div className="Customer-SearchBar-result-meta">
            {hasDiscount ? (
              <>
                <span className="Customer-SearchBar-result-price Customer-SearchBar-price-discount">
                  Rs.{product.discount_price}
                </span>
                <span className="Customer-SearchBar-result-price-strike">
                  Rs.{product.base_price}
                </span>
              </>
            ) : (
              <span className="Customer-SearchBar-result-price">Rs.{product.base_price}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="Customer-SearchBar-overlay-wrapper">
      <div ref={overlayRef} className="Customer-SearchBar-backdrop" onClick={onClose} />

      <div ref={sidebarRef} className="Customer-SearchBar-sidebar">
        <div className="Customer-SearchBar-content">
          <div className="Customer-SearchBar-header">
            <div className="Customer-SearchBar-input-wrapper">
              <input
                ref={inputRef}
                type="text"
                placeholder="Find Your Favourites"
                className="Customer-SearchBar-input"
                value={searchQuery}
                onChange={(e) => {
                  setSelectedSubcategory(null);
                  setSearchQuery(e.target.value);
                }}
              />
              {isLoading ? (
                <Loader2 className="Customer-SearchBar-icon Customer-SearchBar-spin" />
              ) : (
                <Search className="Customer-SearchBar-icon" />
              )}
            </div>
            <button onClick={onClose} className="Customer-SearchBar-close-btn" aria-label="Close search">
              <X />
            </button>
          </div>

          {searchQuery.trim().length === 0 && (
            <div className="Customer-SearchBar-suggestions-section">
              <h3 className="Customer-SearchBar-section-title">Suggestions For You</h3>
              <div className="Customer-SearchBar-suggestions-list">
                {suggestions.map((cat) => (
                  <button
                    key={cat.id}
                    className="Customer-SearchBar-suggestion-item"
                    onClick={() => handleSuggestionClick(cat.category_name)}
                  >
                    {cat.category_name}
                  </button>
                ))}
              </div>

              {featuredProducts.length > 0 && (
                <div className="Customer-SearchBar-results-list">
                  {featuredProducts.map(renderProductCard)}
                </div>
              )}
            </div>
          )}

          {error && <div className="Customer-SearchBar-error">{error}</div>}

          {searchQuery.trim().length > 0 && !isLoading && !error && (
            <div className="Customer-SearchBar-results-section">
              {subcategories.length > 0 && !selectedSubcategory && (
                <div className="Customer-SearchBar-suggestions-section">
                  <h3 className="Customer-SearchBar-section-title">Shop By Category</h3>
                  <div className="Customer-SearchBar-suggestions-list">
                    {subcategories.map((sub) => (
                      <button
                        key={sub.id}
                        className="Customer-SearchBar-suggestion-item"
                        onClick={() => handleSubcategoryClick(sub)}
                      >
                        {sub.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {visibleResults.length > 0 ? (
                <h3 className="Customer-SearchBar-section-title">
                  {`Results for "${searchQuery}"`}
                </h3>
              ) : (
                !(subcategories.length > 0 && !selectedSubcategory) && (
                  <h3 className="Customer-SearchBar-section-title">
                    {`No results found for "${searchQuery}"`}
                  </h3>
                )
              )}

              {visibleResults.length > 0 && (
                <div className="Customer-SearchBar-results-list">
                  {visibleResults.map(renderProductCard)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchDetail;